import { z } from 'zod';

// =============================================================================
// Constraint Schemas
// =============================================================================

/**
 * Constraint type union — discriminant for constraint configs.
 */
export const constraintTypeSchema = z.enum([
  'requiresReferral',
  'leadTime',
  'preferredDays',
  'maxPerDay',
  'maxNewPerDay',
  'buffer',
  'recurringBlock',
  'businessHours',
  'allowWebScheduling',
  'patients',
  'patientGender',
  'cancellationFee',
]);

export type ConstraintType = z.infer<typeof constraintTypeSchema>;

/**
 * Individual constraint config variants.
 */
export const requiresReferralConfigSchema = z.object({
  type: z.literal('requiresReferral'),
  enabled: z.boolean(),
});

export const leadTimeConfigSchema = z.object({
  type: z.literal('leadTime'),
  unit: z.enum(['days', 'hours']),
  limit: z.number(),
  /**
   * How `days` are counted. Only meaningful when `unit === 'days'`:
   *  - `calendar` (default when absent — backward compatible): every day counts.
   *  - `business`: only weekdays (Mon–Fri) count; weekends are skipped.
   * Lead time is always enforced at whole-day (date-boundary) granularity, never
   * as a rolling N×24h window from the exact current instant.
   */
  dayMode: z.enum(['calendar', 'business']).optional(),
});

/**
 * Human-readable unit label for a lead-time config, shared by the settings UI
 * summary and the booking-violation message so the two never drift. Returns
 * "business days" only when days + business mode; otherwise the bare unit.
 */
export function leadTimeUnitLabel(config: { unit?: string; dayMode?: string }): string {
  return config.unit === 'days' && config.dayMode === 'business'
    ? 'business days'
    : (config.unit ?? 'hours');
}

/** A single bookable time window within a day, "HH:MM"–"HH:MM" (24h, location-local). */
export const timeWindowSchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
});

export type TimeWindow = z.infer<typeof timeWindowSchema>;

/**
 * Display-only hint for the visual grid editor: the visible time range and cell
 * size. Purely cosmetic — the slot engine and booking validator ignore it and
 * read `days`/`dayTimes` only.
 */
export const preferredDaysGridSchema = z.object({
  startTime: z.string(), // "07:00"
  endTime: z.string(), // "19:00"
  stepMinutes: z.number(), // 15 | 30 | 60
});

export type PreferredDaysGrid = z.infer<typeof preferredDaysGridSchema>;

export const preferredDaysConfigSchema = z.object({
  type: z.literal('preferredDays'),
  days: z.array(z.string()),
  /**
   * Optional per-day time windows, keyed by the same day token used in `days`
   * (e.g. "Mon"). A day with one or more windows restricts bookable times to
   * those windows; a day absent here (or with an empty array) allows the whole
   * working day. Backward compatible — older configs simply omit this field.
   */
  dayTimes: z.record(z.string(), z.array(timeWindowSchema)).optional(),
  /** Display-only grid editor view settings (see {@link preferredDaysGridSchema}). */
  grid: preferredDaysGridSchema.optional(),
});

// "Booking Limits" — caps the number of bookings per day and/or per week.
// Both fields are optional; an absent field means no limit on that axis.
export const maxPerDayConfigSchema = z.object({
  type: z.literal('maxPerDay'),
  limit: z.number().optional(), // max bookings per day
  weeklyLimit: z.number().optional(), // max bookings per week
});

export const maxNewPerDayConfigSchema = z.object({
  type: z.literal('maxNewPerDay'),
  limit: z.number(),
});

export const bufferConfigSchema = z.object({
  type: z.literal('buffer'),
  beforeMins: z.number(),
  afterMins: z.number(),
});

export const recurringBlockSchema = z.object({
  id: z.string(),
  days: z.array(z.string()),
  startTime: z.string(),
  endTime: z.string(),
  reason: z.string(),
});

export const recurringBlockConfigSchema = z.object({
  type: z.literal('recurringBlock'),
  blocks: z.array(recurringBlockSchema),
});

export const dayScheduleEntrySchema = z.object({
  day: z.string(),
  enabled: z.boolean(),
  startTime: z.string(),
  endTime: z.string(),
});

export const businessHoursConfigSchema = z.object({
  type: z.literal('businessHours'),
  schedule: z.array(dayScheduleEntrySchema),
});

export const allowWebSchedulingConfigSchema = z.object({
  type: z.literal('allowWebScheduling'),
  enabled: z.boolean(),
  webName: z.string(),
});

export const patientsConfigSchema = z.object({
  type: z.literal('patients'),
  patientType: z.enum(['new', 'existing']),
});

export const patientGenderConfigSchema = z.object({
  type: z.literal('patientGender'),
  gender: z.string(),
});

/**
 * A fee the practice charges when a patient cancels this type late (ADR-290).
 * The amount is whole cents so it never carries float drift; the settings UI
 * edits it in dollars. The notice window is not configurable yet — see
 * `CANCELLATION_FEE_NOTICE_HOURS`.
 */
export const cancellationFeeConfigSchema = z.object({
  type: z.literal('cancellationFee'),
  amountCents: z.number().int().positive(),
});

/**
 * How close to the appointment a cancellation has to be for the fee to apply.
 *
 * FIXED FOR NOW, BUT TRAVELLING AS DATA. The API stamps it onto every fee it
 * hands out (`cancellationFeeFromConstraints`), so the voice agent never holds
 * its own copy — making it per-type later is a schema field and this one line.
 */
export const CANCELLATION_FEE_NOTICE_HOURS = 24;

/** A type's cancellation fee as the voice agent consumes it. */
export interface CancellationFee {
  amountCents: number;
  noticeHours: number;
}

export const cancellationFeeSchema = z.object({
  amountCents: z.number().int().positive(),
  noticeHours: z.number().positive(),
});

/**
 * Read the cancellation fee out of an appointment type's raw `constraints`
 * JSONB. Null when the type has none, or when the entry is malformed — a fee
 * nobody can parse is a fee nobody is told about, never a guessed amount.
 */
export function cancellationFeeFromConstraints(constraints: unknown): CancellationFee | null {
  if (!Array.isArray(constraints)) return null;
  for (const entry of constraints) {
    if (typeof entry !== 'object' || entry === null) continue;
    const config = (entry as { config?: unknown }).config;
    const parsed = cancellationFeeConfigSchema.safeParse(config);
    if (parsed.success) {
      return { amountCents: parsed.data.amountCents, noticeHours: CANCELLATION_FEE_NOTICE_HOURS };
    }
  }
  return null;
}

export const constraintConfigSchema = z.discriminatedUnion('type', [
  requiresReferralConfigSchema,
  leadTimeConfigSchema,
  preferredDaysConfigSchema,
  maxPerDayConfigSchema,
  maxNewPerDayConfigSchema,
  bufferConfigSchema,
  recurringBlockConfigSchema,
  businessHoursConfigSchema,
  allowWebSchedulingConfigSchema,
  patientsConfigSchema,
  patientGenderConfigSchema,
  cancellationFeeConfigSchema,
]);

export type ConstraintConfig = z.infer<typeof constraintConfigSchema>;

/**
 * Constraint envelope — each constraint in the JSONB array.
 */
export const constraintSchema = z.object({
  id: z.string(),
  type: constraintTypeSchema,
  label: z.string(),
  config: constraintConfigSchema,
});

export type Constraint = z.infer<typeof constraintSchema>;

// =============================================================================
// Appointment Type Response
// =============================================================================

export const appointmentTypeResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  description: z.string().nullable(),
  durationMinutes: z.number(),
  doubleBooking: z.number(),
  category: z.string().nullable(),
  color: z.string().nullable(),
  requiresReferral: z.boolean(),
  isGeneric: z.boolean(),
  serviceCodes: z.array(z.string()).nullable(),
  icdCodes: z.array(z.string()).nullable(),
  constraints: z.array(z.unknown()),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type AppointmentTypeResponse = z.infer<typeof appointmentTypeResponseSchema>;

// =============================================================================
// Create Appointment Type Request
// =============================================================================

export const createAppointmentTypeSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  shortName: z.string().min(1),
  durationMinutes: z.number().int().min(5).max(480),
  color: z.string().min(1),
  description: z.string().nullable().optional(),
  doubleBooking: z.number().int().min(0).optional(),
  category: z.string().nullable().optional(),
});

export type CreateAppointmentTypeRequest = z.infer<typeof createAppointmentTypeSchema>;

// =============================================================================
// Update Appointment Type Request
// =============================================================================

export const updateAppointmentTypeSchema = z.object({
  shortName: z.string().nullable().optional(),
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  durationMinutes: z.number().int().min(5).max(480).optional(),
  doubleBooking: z.number().int().min(0).optional(),
  category: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  serviceCodes: z.array(z.string()).nullable().optional(),
  icdCodes: z.array(z.string()).nullable().optional(),
  constraints: z.array(z.unknown()).optional(),
});

export type UpdateAppointmentTypeRequest = z.infer<typeof updateAppointmentTypeSchema>;

// =============================================================================
// Appointment Type Override
// =============================================================================

export const appointmentTypeOverrideResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  appointmentTypeId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid().nullable(),
  enabled: z.boolean(),
  durationOverride: z.number().nullable(),
  constraints: z.record(z.string(), z.unknown()),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type AppointmentTypeOverrideResponse = z.infer<typeof appointmentTypeOverrideResponseSchema>;

export const upsertOverrideSchema = z.object({
  appointmentTypeId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid().nullable().optional(),
  enabled: z.boolean(),
  durationOverride: z.number().int().min(5).max(480).nullable().optional(),
  constraints: z.record(z.string(), z.unknown()).optional(),
});

export type UpsertOverrideRequest = z.infer<typeof upsertOverrideSchema>;

// =============================================================================
// Appointment Type Eligibility (voice agent query result)
// =============================================================================

export const appointmentTypeEligibilitySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid(),
  appointmentTypeId: z.string().uuid(),
  enabled: z.boolean(),
  durationMinutes: z.number(),
  requiresReferral: z.boolean(),
  patientType: z.string().nullable(),
  allowWebScheduling: z.boolean(),
  appointmentTypeName: z.string(),
  appointmentTypeCode: z.string(),
  appointmentTypeCategory: z.string().nullable(),
  appointmentTypeColor: z.string().nullable(),
  constraints: z.record(z.string(), z.unknown()),
  computedAt: z.string(),
});

export type AppointmentTypeEligibilityResponse = z.infer<typeof appointmentTypeEligibilitySchema>;
